import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { tokenStore } from "@/lib/api";

const AuthContext = createContext(null);

// Status som ger full access till appen. Måste matcha
// backend/routes_stripe.py:ACCESS_ALLOWED_STATUSES
const ALLOWED_STATUSES = new Set(["trialing", "active", "grandfathered"]);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshSubscription = useCallback(async () => {
    try {
      const r = await api.get("/stripe/subscription");
      setSubscription(r.data);
      return r.data;
    } catch {
      setSubscription(null);
      return null;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      await refreshSubscription();
    } catch {
      // Try refresh once
      try {
        await api.post("/auth/refresh");
        const r2 = await api.get("/auth/me");
        setUser(r2.data);
        await refreshSubscription();
      } catch {
        setUser(null);
        setSubscription(null);
      }
    } finally {
      setLoading(false);
    }
  }, [refreshSubscription]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Lyssna på globalt `auth:expired`-event från axios-interceptorn: när
  // refresh definitivt misslyckats (t.ex. refresh-token utgången efter 7 dagar)
  // loggar vi ut användaren och skickar dem till /login istället för att visa
  // råa 401-fel i UI:t.
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setSubscription(null);
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    };
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, []);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    // Spara bearer-fallback-token (behövs för iPad Safari + cross-origin iframe
    // där third-party cookies blockeras av ITP). Cookies sätts fortfarande av
    // backenden — bearer är bara backup.
    if (r.data?.access_token || r.data?.refresh_token) {
      tokenStore.set(r.data.access_token, r.data.refresh_token);
    }
    setUser(r.data);
    await refreshSubscription();
    return r.data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    tokenStore.clear();
    setUser(null);
    setSubscription(null);
    window.location.href = "/login";
  };

  const subscriptionActive = subscription
    ? ALLOWED_STATUSES.has(subscription.subscription_status)
    : false;

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        checkAuth,
        login,
        logout,
        subscription,
        subscriptionActive,
        refreshSubscription,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
